import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';

import _ from 'lodash';
import { ScopeTemplate } from '../../types/ScopeTemplate';
import { addScopeTemplateToUser, getScopeTemplatesAssignedToUser, removeScopeTemplateFromUser } from '../../services/scopetemplate.service';
import { addValueToUserScope, getScopeValuesForUser, removeValueFromUserScope } from '../../services/scope.service';
import { SingleResult } from '../../types/SingleResult';

interface UserScopeState {
  assignedTemplates: ScopeTemplate[];
  isAssignedTemplatesLoading: boolean;
  assignedScopeValues: ScopeValue[];
  isAssignedScopeValuesLoading: boolean;
}

const initialState: UserScopeState = {
  assignedTemplates: [],
  isAssignedTemplatesLoading: false,
  assignedScopeValues: [],
  isAssignedScopeValuesLoading: false,
};

export const assignScopeTemplateToUser = createAsyncThunk(
  'userScope/assignScopeTemplateToUser',
  async (param: any) => await addScopeTemplateToUser(param.userId, param.templateId)
);
export const unassignScopeTemplateFromUser = createAsyncThunk(
  'userScope/unassignScopeTemplateFromUser',
  async (param: any) => await removeScopeTemplateFromUser(param.userId, param.templateId)
);
export const fetchScopeTemplatesAssignedToUser = createAsyncThunk(
  'userScope/fetchScopeTemplatesAssignedToUser',
  async (param: any) => await getScopeTemplatesAssignedToUser(param)
);
export const fetchScopeValuesForUser = createAsyncThunk(
  'userScope/fetchScopeValuesForUser',
  async (param: any) => await getScopeValuesForUser(param.userId, param.scopeName)
);
export const assignValueToUserScope = createAsyncThunk(
  'userScope/assignValueToUserScope',
  async (param: any) => await addValueToUserScope(param.userId, param.scopeName, param.value)
);
export const unassignValueFromUserScope = createAsyncThunk(
  'userScope/unassignValueFromUserScope',
  async (param: any) => await removeValueFromUserScope(param.userId, param.scopeName, param.value)
);

export const userScopeSlice = createSlice({
  name: 'userScope',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchScopeTemplatesAssignedToUser.pending, (state) => {
        state.isAssignedTemplatesLoading = true;
      })
      .addCase(fetchScopeTemplatesAssignedToUser.fulfilled, (state, action: PayloadAction<SingleResult<ScopeTemplate[]>>) => {
        if (action.payload.data) {
          state.assignedTemplates = action.payload.data;
        }
        state.isAssignedTemplatesLoading = false;
      })
      .addCase(fetchScopeValuesForUser.pending, (state) => {
        state.isAssignedScopeValuesLoading = true;
      })
      .addCase(fetchScopeValuesForUser.fulfilled, (state, action: PayloadAction<SingleResult<ScopeValue[]>>) => {
        state.assignedScopeValues = action.payload.data;
        state.isAssignedScopeValuesLoading = false;
      })
      .addCase(assignScopeTemplateToUser.pending, (state) => {
        state.isAssignedTemplatesLoading = true;
      })
      .addCase(assignScopeTemplateToUser.fulfilled, (state, action: PayloadAction<SingleResult<ScopeTemplate[]>>) => {
        state.assignedTemplates = action.payload.data;
        state.isAssignedTemplatesLoading = false;
      })
      .addCase(unassignScopeTemplateFromUser.pending, (state) => {
        state.isAssignedTemplatesLoading = true;
      })
      .addCase(unassignScopeTemplateFromUser.fulfilled, (state, action: PayloadAction<SingleResult<ScopeTemplate[]>>) => {
        state.assignedTemplates = action.payload.data;
        state.isAssignedTemplatesLoading = false;
      })
      .addCase(assignValueToUserScope.pending, (state) => {
        state.isAssignedScopeValuesLoading = true;
      })
      .addCase(assignValueToUserScope.fulfilled, (state, action: PayloadAction<SingleResult<ScopeValue[]>>) => {
        state.assignedScopeValues = action.payload.data;
        state.isAssignedScopeValuesLoading = false;
      })
      .addCase(unassignValueFromUserScope.pending, (state) => {
        state.isAssignedScopeValuesLoading = true;
      })
      .addCase(unassignValueFromUserScope.fulfilled, (state, action: PayloadAction<SingleResult<ScopeValue[]>>) => {
        state.assignedScopeValues = action.payload.data;
        state.isAssignedScopeValuesLoading = false;
      });
  },
});

export default userScopeSlice.reducer;
